import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { AccessoriesService } from 'src/app/services/accessories/accessories.service';
import { AccessoriesModalService } from 'src/app/services/accessories-modal/accessories-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-accessories',
  templateUrl: './accessories.component.html',
  styleUrls: ['./accessories.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class AccessoriesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  accessories: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedAccessories: string[] = [];

  constructor(
    private _accessoriesService: AccessoriesService,
    private _accessoriesModalService: AccessoriesModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadAccessories();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadAccessories(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedAccessories = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._accessoriesService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.accessories = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.accessories = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading accessories:', error);
          this.accessories = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadAccessories(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadAccessories(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadAccessories();
  }

  openAddAccessoryModal(): void {
    this._accessoriesModalService.clearSelectedAccessory();
    const modalElement = document.getElementById('kt_modal_accessory_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(accessory: any): void {
    this._accessoriesModalService.setSelectedAccessory(accessory);
    const modalElement = document.getElementById('kt_modal_accessory_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteAccessory(accessory: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${accessory.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(accessory);
      }
    });
  }

  private performDelete(accessory: any): void {
    const accessoryId = accessory._id || accessory.id;
    this.subscriptions.push(
      this._accessoriesService.DeleteDataById(accessoryId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Accessory has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadAccessories(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete accessory.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting accessory.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onAccessoryAdded(): void {
    console.log('Accessory added, refreshing list...');
    this.loadAccessories(this.currentPage, this.searchTerm, this.filters);
  }

  onAccessoryUpdated(): void {
    console.log('Accessory updated, refreshing list...');
    this.loadAccessories(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(accessoryId: string): boolean {
    return this.selectedAccessories.includes(accessoryId);
  }

  isAllSelected(): boolean {
    return this.accessories.length > 0 && this.selectedAccessories.length === this.accessories.length;
  }

  isIndeterminate(): boolean {
    return this.selectedAccessories.length > 0 && this.selectedAccessories.length < this.accessories.length;
  }

  toggleSelection(accessoryId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedAccessories.includes(accessoryId)) {
        this.selectedAccessories.push(accessoryId);
      }
    } else {
      this.selectedAccessories = this.selectedAccessories.filter(id => id !== accessoryId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedAccessories = this.accessories.map(accessory => accessory._id);
    } else {
      this.selectedAccessories = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-accessory-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-accessory-table-toolbar="selected"]');

    if (this.selectedAccessories.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedAccessories(): void {
    if (this.selectedAccessories.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedAccessories.length} selected accessory(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedAccessories.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._accessoriesService.BulkDelete(this.selectedAccessories).subscribe({
        next: () => {
          this.loadAccessories();
          this.selectedAccessories = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected accessories have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting accessories:', error);
          Swal.fire('Error!', 'Failed to delete selected accessories.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
